import { PDF } from '@/components/PDF';
import React, { useState } from 'react';
import { Button } from 'semantic-ui-react';
import { useAsyncEffect } from './useAsyncEffect';

export const useRenderFile = ({
  fileExtension,
  fileLocation,
  blob,
  fileName,
  azureBlobName,
}: {
  fileExtension: string | undefined;
  fileLocation: string | undefined;
  blob: Blob | undefined;
  fileName: string | undefined;
  azureBlobName: string | undefined;
}) => {
  const [renderedFile, setRenderedFile] = useState<React.ReactNode>();
  const isWord = fileExtension === 'doc' || fileExtension === 'docx';
  const isExcel = fileExtension === 'xls' || fileExtension === 'xlsx';
  const isPDF = fileExtension === 'pdf';
  const isImage = fileExtension === 'png' || fileExtension === 'jpg' || fileExtension === 'jpeg';

  useAsyncEffect(async () => {
    // Reset rendered file when inputs change
    setRenderedFile(undefined);

    // Need either blob or fileLocation to render
    if (!blob && !fileLocation) {
      return;
    }

    let fileUrl: string | undefined;
    let file: File | undefined;

    // Create file URL from blob if available
    if (blob) {
      file = new File([blob], fileName ?? '');
      fileUrl = URL.createObjectURL(file);
    } else if (fileLocation) {
      // Use the provided file location URL
      fileUrl = fileLocation;
    }

    if (fileExtension === 'pdf') {
      if (file) {
        setRenderedFile(<PDF file={file} />);
      } else if (blob) {
        // We have a blob, create a File object
        const pdfFile = new File([blob], fileName ?? '', { type: 'application/pdf' });
        setRenderedFile(<PDF file={pdfFile} />);
      } else {
        setRenderedFile(
          'PDF must be loaded as a blob from the server. URL-based PDF loading is not supported due to CORS restrictions.',
        );
      }
      return;
    }

    if (
      (fileExtension === 'png' || fileExtension === 'jpg' || fileExtension === 'jpeg') &&
      fileUrl
    ) {
      setRenderedFile(
        <img src={fileUrl} alt={fileName} style={{ maxWidth: '100%', height: 'auto' }} />,
      );
      return;
    }

    if (isWord || isExcel) {
      const handleDownload = () => {
        if (fileUrl) {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = fileName || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      };

      setRenderedFile(
        <Button color="blue" disabled={!fileUrl} onClick={handleDownload}>
          Download File
        </Button>,
      );
      return;
    }

    // If we have a file extension but it's not supported
    if (fileExtension) {
      setRenderedFile('Invalid file type');
      return;
    }

    // If no azure blob name and no extension
    if (!azureBlobName) {
      setRenderedFile('No file...');
      return;
    }

    setRenderedFile('Unable to determine file type');
  }, [blob, fileLocation, fileExtension, fileName, azureBlobName]);

  return { renderedFile, isWord, isExcel, isPDF, isImage };
};
